/**
 * Root Route - The top-level route that wraps all other routes in the application.
 *
 * This file is responsible for:
 * 1. Bootstrapping user permissions after MSAL authentication
 * 2. Showing a loading state while permissions are being fetched
 * 3. Providing the app layout shell (header, sidebar, footer)
 * 4. Handling cross-tab logout synchronization
 * 5. Catching and displaying unhandled route-level errors
 */

// TanStack Router imports
import {
  createRootRoute, // Factory function to create the root route definition
  Outlet, // Renders the matched child route's component
  useRouter, // Hook to access the router instance (for getting current path)
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router"; // Type for error component props

// MSAL (Microsoft Authentication Library) React hook
import { useMsal } from "@azure/msal-react"; // Provides accounts array and auth state

// Custom hooks and stores
import { useAuthenticatedApiClient } from "../hooks/useAuthenticatedApiClient"; // Wraps API calls with token acquisition
import { useAdminStore, setAdminApiClient } from "../stores/adminStore"; // Zustand store for user/permissions data

// UI Components
import { AppLayout } from "../components/layout/AppLayout"; // Main app shell (header, sidebar, footer)
import { PermissionsLoader } from "../components/PermissionsLoader"; // Full-screen loading spinner shown during bootstrap

// React imports
import { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "react-bootstrap";
import { Link } from "@tanstack/react-router";
import { FlehsPage } from "../components/FlehsPage"; // Standard page wrapper with title/preTitle
import { IconAlertTriangle, IconRefresh, IconHome } from "@tabler/icons-react";

// TanStack Router Devtools - uncomment for debugging route state
// import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";

/**
 * Root Route Definition
 *
 * createRootRoute() creates the root of the route tree.
 * - component: The React component to render (wraps all child routes)
 * - errorComponent: Displayed when an unhandled error occurs in any child route
 */
export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});

/**
 * RootComponent - Main application wrapper
 *
 * This component:
 * 1. Checks if user is authenticated (has MSAL accounts)
 * 2. Fetches user permissions from the API
 * 3. Shows PermissionsLoader while fetching
 * 4. Renders AppLayout + child routes once ready
 * 5. Syncs logout state across browser tabs
 */
function RootComponent() {
  // Get authenticated accounts from MSAL
  // accounts.length > 0 means user is logged in
  // accounts[0].idTokenClaims contains user info like OID (Object ID)
  const { accounts } = useMsal();

  // Redirect unauthenticated users away from protected routes
  //
  // NOTE: We explicitly list protected paths rather than using `pathname !== "/"`
  // because MSAL's logoutPopup opens a popup window that loads the app. In Chrome
  // incognito mode, stricter cookie/storage policies break MSAL's cross-window
  // communication, causing the popup to stay open and render the app. If we used
  // `pathname !== "/"`, the popup would trigger a redirect, breaking the logout flow.
  // By only checking specific module paths, we avoid interfering with MSAL popups.
  const isProtectedPath =
    /^\/(mosquito|sentinel|veterinary|admin|profile)/.test(
      window.location.pathname
    );
  if (accounts.length === 0 && isProtectedPath) {
    window.location.href = "/";
    return null;
  }

  // API client with automatic token acquisition
  // This will attempt acquireTokenSilent before each request
  const apiClient = useAuthenticatedApiClient("flehsApi");

  // Zustand store action to fetch user details (includes permissions)
  const fetchUserById = useAdminStore((s) => s.fetchUserById);

  // Tracks whether permissions have been successfully loaded
  // Used to control showing PermissionsLoader vs actual content
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Ref to track which user we're currently loading permissions for
  // Prevents duplicate API calls when useEffect runs multiple times
  // Using ref instead of state because we need synchronous updates
  const loadingForUserRef = useRef<string | null>(null);

  /**
   * Cross-tab logout sync handler
   *
   * When user logs out in another tab, the Zustand store (persisted to localStorage)
   * is cleared. This handler detects that change and reloads the page to sync state.
   *
   * Why reload instead of just clearing state?
   * MSAL's in-memory accounts array doesn't sync automatically when localStorage
   * changes from another tab. A full reload re-initializes MSAL properly.
   */
  const handleStorageChange = useCallback((event: StorageEvent) => {
    // Only react to changes in our admin store's localStorage key
    if (event.key !== "flehs-admin-store") return;

    try {
      // Parse the new localStorage value
      const newValue = event.newValue ? JSON.parse(event.newValue) : null;
      const userDetail = newValue?.state?.userDetail;

      // If userDetail is empty or null, user logged out in another tab
      if (!userDetail || Object.keys(userDetail).length === 0) {
        // Force full page reload to re-initialize MSAL and clear stale state
        window.location.href = "/";
      }
    } catch {
      // Ignore JSON parse errors (malformed data)
    }
  }, []);

  /**
   * Set up cross-tab logout listener
   *
   * The 'storage' event fires when localStorage is modified by ANOTHER tab.
   * It does NOT fire for changes made in the same tab.
   */
  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    // Cleanup: remove listener when component unmounts
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleStorageChange]);

  /**
   * Permission loading effect
   *
   * Runs when the user logs in (accounts.length changes from 0 to 1).
   * Fetches user details from the API, which includes their roles and permissions.
   */
  useEffect(() => {
    // Extract the user's Azure AD Object ID from the ID token claims
    // This is the unique identifier for the user in our system
    const oid =
      accounts.length > 0 ? (accounts[0].idTokenClaims as any)?.oid : null;

    // Guard: Skip if user is not authenticated
    if (!accounts.length || !apiClient || !oid) {
      return;
    }

    // Guard: Skip if we're already loading/loaded for this user
    // Prevents duplicate API calls when React re-renders
    if (loadingForUserRef.current === oid) {
      return;
    }

    // Mark as loading for this user IMMEDIATELY (synchronous)
    // Must be before any async code to prevent race conditions
    loadingForUserRef.current = oid;

    const loadPermissions = async () => {
      try {
        // Register the API client with the admin store
        // This allows the store's actions to make authenticated API calls
        setAdminApiClient(apiClient);

        // Fetch user data and enforce minimum 2-second loader display
        // The 2-second minimum ensures the loader is visible long enough
        // to prevent jarring flash-of-content on fast connections
        await Promise.all([
          fetchUserById(oid), // Fetches user details including roles/permissions
          new Promise((resolve) => setTimeout(resolve, 2000)), // Minimum display time
        ]);

        // Mark permissions as loaded - this hides the loader
        setPermissionsLoaded(true);
      } catch (error) {
        console.error("Failed to load user permissions:", error);

        // Reset ref so user can retry (e.g., after fixing network issues)
        loadingForUserRef.current = null;

        // Clear MSAL cache and redirect to home - let the user log in again
        window.location.href = "/";
      }
    };

    loadPermissions();

    // Dependency array notes:
    // - Only depend on accounts.length, not the full accounts array
    // - apiClient ref changes on every render, so we exclude it to prevent loops
    // - The ref (loadingForUserRef) handles preventing duplicate calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  /**
   * Render logic
   *
   * If user is authenticated but permissions haven't loaded yet,
   * show the full-screen loading spinner. Otherwise, render the app.
   */
  if (accounts.length > 0 && !permissionsLoaded) {
    return <PermissionsLoader />;
  }

  return (
    <>
      {/* AppLayout provides the app shell: header, sidebar, footer */}
      <AppLayout>
        {/* Outlet renders the matched child route's component */}
        <Outlet />
      </AppLayout>

      {/* TanStack Router Devtools - uncomment for debugging */}
      {/* <TanStackRouterDevtools /> */}
    </>
  );
}

/**
 * RootErrorComponent - Global error boundary for routes
 *
 * This component is displayed when an unhandled error occurs in any route.
 * TanStack Router catches the error and renders this instead of crashing.
 * Uses full page reload instead of reset() since errors here are typically stale module imports.
 *
 * @param error - The caught error object
 */
function RootErrorComponent({ error }: ErrorComponentProps) {
  // Access router to get the current path where the error occurred
  const router = useRouter();
  const currentPath = router.state.location.pathname;

  // Detect network errors to show appropriate messaging
  const isNetworkError =
    error instanceof TypeError ||
    error.message?.toLowerCase().includes("network") ||
    error.message?.toLowerCase().includes("fetch") ||
    error.message?.toLowerCase().includes("failed to fetch");

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <AppLayout>
      <FlehsPage preTitle="Florida Environmental Health" title="Error">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <Card className="card-lg border-0">
              <Card.Body className="text-center py-5">
                {/* Error icon */}
                <div className="mb-4">
                  <IconAlertTriangle
                    size={40}
                    stroke={1.5}
                    className="text-danger"
                  />
                </div>

                {/* Error title */}
                <h2 className="mb-2">Something Went Wrong</h2>

                {/* User-friendly error message */}
                <p className="text-muted mb-4">
                  {isNetworkError
                    ? "Unable to connect to the server. Please check your internet connection and try again."
                    : "An unexpected error occurred while loading this page."}
                </p>

                {/* Technical error details - only shown in development */}
                {import.meta.env.DEV && (
                  <div className="alert text-start mb-4 border-0">
                    <div className="d-flex align-items-start">
                      <div>
                        <strong>Error:</strong>
                        <pre
                          className="mb-0 mt-1 small"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {error.message || String(error)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show path where error occurred */}
                <p className="text-muted small mb-4">
                  Error occurred at: <code>{currentPath}</code>
                </p>

                {/* Action buttons */}
                <div className="btn-list justify-content-center">
                  {/* Try Again - always reloads the page to get fresh modules */}
                  <button onClick={handleRetry} className="btn btn-primary">
                    <IconRefresh className="icon me-1" />
                    Try Again
                  </button>

                  {/* Go Home - navigate away from broken route */}
                  <Link to="/" className="btn btn-outline-secondary">
                    <IconHome className="icon me-1" />
                    Go Home
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </FlehsPage>
    </AppLayout>
  );
}
